/**
 * Authentication and Authorization Types
 * Defines roles, user types, permission structures, and RBAC
 *
 * Updated to align with DB schema: security.users, security.roles, security.permissions
 */

import type { UserRole, UserStatus } from './user.types'
import type { TenantType, PlanCode } from './tenant.types'
import { getRoleLabel, normalizeRole, resolveRole } from '@/security/accessContract'

// Re-export for convenience
export type { UserRole, UserStatus } from './user.types'

// ============================================================================
// LEGACY ENUMS (for backward compatibility)
// ============================================================================

/**
 * @deprecated Use UserRole type from user.types.ts instead
 * Kept for backward compatibility during migration
 */
export enum LegacyUserRole {
  OWNER = 'owner',
  ASSOCIATE = 'associate',
  CLERK = 'clerk',
  CLIENT = 'client',
}

/**
 * @deprecated Use UserStatus type from user.types.ts instead
 */
export enum LegacyUserStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  ARCHIVED = 'archived',
  PENDING = 'pending',
}

/**
 * Map legacy roles to new roles
 */
export function mapLegacyRole(legacyRole: LegacyUserRole | string): UserRole {
  return normalizeRole(legacyRole)
}

// ============================================================================
// AUTHENTICATED USER
// ============================================================================

/**
 * Authenticated User
 * Represents the current logged-in user with tenant context
 */
export interface AuthUser {
  authScope?: string
  controllers?: Array<{
    client_id: string
    controller_role: string
    actions: string[]
    client_type?: string
  }>
  id: string
  email: string
  username?: string
  firstName: string
  lastName: string
  fullName: string
  role: UserRole
  roles?: UserRole[]
  permissions?: string[]
  features?: string[]
  roleCompatibility?: Array<Record<string, unknown>>
  requiresRoleClassification?: boolean
  accessContractVersion?: string
  status: UserStatus
  isVerified: boolean | null

  // Tenant context
  tenantId: string
  tenantCode: string
  tenantName: string
  tenantType: TenantType

  // Subscription context
  planCode: PlanCode

  // Profile
  avatarUrl?: string
  phone?: string

  // Session info
  lastLoginAt?: string
  currentSessionId?: string

  // Timestamps
  createdAt: string | null
  updatedAt: string | null
}

// ============================================================================
// LOGIN/LOGOUT TYPES
// ============================================================================

/**
 * Login Request
 */
export interface LoginRequest {
  email: string
  password: string
  rememberMe?: boolean
}

/**
 * Login Response
 */
export interface LoginResponse {
  user: AuthUser
  token: string
  refreshToken: string
  expiresIn: number
  sessionId: string
}

/**
 * Logout Request
 */
export interface LogoutRequest {
  sessionId?: string
  allDevices?: boolean
}

// ============================================================================
// PASSWORD RESET TYPES
// ============================================================================

/**
 * Forgot Password Request
 */
export interface ForgotPasswordRequest {
  email: string
}

/**
 * Reset Password Request
 */
export interface ResetPasswordRequest {
  token: string
  newPassword: string
  confirmPassword: string
}

/**
 * Change Password Request
 */
export interface ChangePasswordRequest {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

// ============================================================================
// SESSION TYPES
// ============================================================================

/**
 * Auth Session State
 * Represents the current authentication state in the app
 */
export interface AuthSession {
  user: AuthUser | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  sessionId: string | null
}

/**
 * Initial auth session state
 */
export const INITIAL_AUTH_SESSION: AuthSession = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  sessionId: null,
}

// ============================================================================
// AUTH CONTEXT TYPE
// ============================================================================

/**
 * Auth Context Type
 */
export interface AuthContextType {
  session: AuthSession
  login: (
    email: string,
    password: string,
    rememberMe?: boolean,
    mfaCode?: string,
    independent?: boolean
  ) => Promise<void>
  logout: (allDevices?: boolean) => Promise<void>
  isLoggedIn: boolean
  user: AuthUser | null
  hasRole: (roles: UserRole | UserRole[]) => boolean
  hasPermission: (permission: string) => boolean
  canAccessFeature: (feature: string) => boolean
  refreshSession: () => Promise<void>
  renewPlatformMfa?: (code: string) => Promise<void>
  adoptTokens?: (
    data: { access_token: string; refresh_token?: string; session_id?: string },
    email?: string
  ) => Promise<void>
}

// ============================================================================
// PERMISSION TYPES
// ============================================================================

/**
 * Permission
 * Matches: security.permissions table
 */
export interface Permission {
  permissionId: string
  permissionKey: string
  description?: string
  category?: string
}

/**
 * Role
 * Matches: security.roles table
 */
export interface Role {
  roleId: string
  roleName: string
  roleType: 'predefined' | 'custom'
  description?: string
  isSystemRole: boolean
  permissions: string[]
}

/**
 * Permission Categories
 */
export const PERMISSION_CATEGORIES = [
  'users',
  'roles',
  'clients',
  'documents',
  'assessments',
  'reports',
  'settings',
  'billing',
] as const

export type PermissionCategory = (typeof PERMISSION_CATEGORIES)[number]

// ============================================================================
// RBAC PERMISSIONS MAP
// ============================================================================

/**
 * Role Permissions Map
 * Defines which permissions each role has
 */
/** @deprecated Real authorization uses effective permissions returned by login. */
export const ROLE_PERMISSIONS: Partial<Record<UserRole, string[]>> = {
  owner: [
    // User Management
    'txh.user.create',
    'txh.user.read',
    'txh.user.update',
    'txh.user.delete',
    // Role Management
    'txh.role.create',
    'txh.role.read',
    'txh.role.update',
    'txh.role.delete',
    'txh.role.assign',
    // Client Management
    'txh.client.create',
    'txh.client.read',
    'txh.client.update',
    'txh.client.delete',
    // Document Management
    'txh.document.upload',
    'txh.document.read',
    'txh.document.delete',
    // Assessment Management
    'txh.assessment.create',
    'txh.assessment.read',
    'txh.assessment.update',
    'txh.assessment.approve',
    // Reports
    'txh.reports.view',
    'txh.reports.export',
    // Settings
    'txh.settings.read',
    'txh.settings.update',
    // Billing
    'txh.settings.billing',
    // Audit
    'txh.audit.view',
  ],
  admin: [
    // User Management
    'txh.user.create',
    'txh.user.read',
    'txh.user.update',
    // Role Management
    'txh.role.read',
    'txh.role.assign',
    // Client Management
    'txh.client.create',
    'txh.client.read',
    'txh.client.update',
    'txh.client.delete',
    // Document Management
    'txh.document.upload',
    'txh.document.read',
    'txh.document.delete',
    // Assessment Management
    'txh.assessment.create',
    'txh.assessment.read',
    'txh.assessment.update',
    'txh.assessment.approve',
    // Reports
    'txh.reports.view',
    'txh.reports.export',
    // Settings
    'txh.settings.read',
    // Audit
    'txh.audit.view',
  ],
  partner: [
    // Client Management
    'txh.client.create',
    'txh.client.read',
    'txh.client.update',
    // Document Management
    'txh.document.upload',
    'txh.document.read',
    // Assessment Management
    'txh.assessment.create',
    'txh.assessment.read',
    'txh.assessment.update',
    'txh.assessment.approve',
    // Reports
    'txh.reports.view',
    'txh.reports.export',
  ],
  assistant: [
    // Client Management (read only)
    'txh.client.read',
    // Document Management
    'txh.document.upload',
    'txh.document.read',
    // Assessment Management (limited)
    'txh.assessment.read',
    'txh.assessment.update',
  ],
  staff: [
    // Client Management (read only)
    'txh.client.read',
    // Document Management (read only)
    'txh.document.read',
    // Assessment Management (read only)
    'txh.assessment.read',
  ],
  client: [
    // Document Management (own documents only)
    'txh.document.upload',
    'txh.document.read',
  ],
}

// ============================================================================
// RBAC HELPER FUNCTIONS
// ============================================================================

/**
 * Check if a role has a specific permission
 */
export function roleHasPermission(role: UserRole, permission: string): boolean {
  const permissions = ROLE_PERMISSIONS[role] || []
  return (
    permissions.includes(permission) ||
    permissions.some(p => {
      // Support wildcard permissions like 'txh.user.*'
      if (p.endsWith('.*')) {
        const prefix = p.slice(0, -1)
        return permission.startsWith(prefix)
      }
      return false
    })
  )
}

/**
 * Get all permissions for a role
 */
export function getRolePermissions(role: UserRole): string[] {
  return ROLE_PERMISSIONS[role] || []
}

/**
 * Check if user can manage other users
 */
export function canManageUsers(role: UserRole): boolean {
  return roleHasPermission(role, 'txh.user.create') || roleHasPermission(role, 'txh.user.update')
}

/**
 * Check if user can view audit logs
 */
export function canViewAuditLogs(role: UserRole): boolean {
  return roleHasPermission(role, 'txh.audit.view')
}

/**
 * Check if user can manage billing
 */
export function canManageBilling(role: UserRole): boolean {
  return roleHasPermission(role, 'txh.settings.billing')
}

/**
 * Check if user can update settings
 */
export function canUpdateSettings(role: UserRole): boolean {
  return roleHasPermission(role, 'txh.settings.update')
}

// ============================================================================
// CASE/CLIENT VISIBILITY
// ============================================================================

/**
 * Case/Client Visibility Rules
 */
export enum DataVisibility {
  ALL = 'all', // Owner/Admin sees all
  ASSIGNED = 'assigned', // Partner/Assistant see assigned only
  OWN = 'own', // Client sees own data only
  NONE = 'none', // No access
}

/**
 * Get data visibility for role
 */
export function getDataVisibilityForRole(role: UserRole): DataVisibility {
  switch (resolveRole(role).canonicalRole) {
    case 'firm_owner':
    case 'firm_admin':
      return DataVisibility.ALL
    case 'ca_partner':
    case 'technical_assistant':
    case 'research_assistant':
      return DataVisibility.ASSIGNED
    case 'individual_client':
    case 'business_owner_client':
    case 'authorized_client':
      return DataVisibility.OWN
    default:
      return DataVisibility.NONE
  }
}

// ============================================================================
// ROLE DISPLAY HELPERS
// ============================================================================

/**
 * Get role display name
 */
export function getRoleDisplayName(role: UserRole): string {
  return getRoleLabel(role)
}

/**
 * Get role badge color class
 */
export function getRoleBadgeClass(role: UserRole): string {
  const classes: Partial<Record<UserRole, string>> = {
    firm_owner: 'badge-primary',
    firm_admin: 'badge-secondary',
    ca_partner: 'badge-accent',
    technical_assistant: 'badge-info',
    research_assistant: 'badge-warning',
    individual_client: 'badge-outline',
    business_owner_client: 'badge-outline',
    authorized_client: 'badge-outline',
    independent_client: 'badge-outline',
  }
  return classes[normalizeRole(role)] || 'badge-ghost'
}

// ============================================================================
// NAVIGATION & FEATURE ACCESS
// ============================================================================

/**
 * Navigation items visibility by role
 */
export function getNavItemsForRole(role: UserRole): string[] {
  const baseItems = ['dashboard', 'customers', 'search']

  switch (role) {
    case 'owner':
      return [...baseItems, 'users', 'sessions', 'audit', 'settings', 'billing']
    case 'admin':
      return [...baseItems, 'users', 'sessions', 'audit', 'settings']
    case 'partner':
      return [...baseItems, 'reports', 'settings']
    case 'assistant':
    case 'staff':
      return [...baseItems, 'settings']
    case 'client':
      return ['dashboard', 'documents', 'settings']
    default:
      return baseItems
  }
}

/**
 * Dashboard sections visibility by role
 */
export function getDashboardSectionsForRole(role: UserRole): Record<string, boolean> {
  const effectiveRole = normalizeRole(role)
  const isClient =
    effectiveRole === 'individual_client' ||
    effectiveRole === 'business_owner_client' ||
    effectiveRole === 'authorized_client' ||
    effectiveRole === 'client'

  return {
    overview: true,
    metrics: !isClient,
    recentActivity: !isClient,
    pendingTasks: !isClient,
    quickActions: true,
    userManagement: effectiveRole === 'firm_owner' || effectiveRole === 'firm_admin',
    billing: effectiveRole === 'firm_owner',
  }
}

// ============================================================================
// STORAGE KEYS
// ============================================================================

export const AUTH_STORAGE_KEYS = {
  TOKEN: 'TaxHive_auth_token',
  REFRESH_TOKEN: 'TaxHive_refresh_token',
  USER: 'TaxHive_auth_user',
  SESSION_ID: 'TaxHive_session_id',
  REMEMBER_ME: 'TaxHive_remember_me',
} as const
